import { Link } from 'react-router-dom';
import type { CatalogItemSummary } from '../types';
import { TagList } from './TagList';

interface CatalogCardProps {
  item: CatalogItemSummary;
}

export function CatalogCard({ item }: CatalogCardProps) {
  const className =
    'card group flex min-h-[18rem] flex-col overflow-hidden transition duration-200 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200/70';

  const inner = (
    <article data-testid="catalog-card" className="flex h-full flex-col">
      {item.image_url ? (
        <img
          src={item.image_url}
          alt=""
          className="h-36 w-full object-cover transition duration-200 group-hover:scale-[1.02]"
        />
      ) : (
        <div className="flex h-36 items-center justify-center bg-gradient-to-br from-primary-50 via-white to-indigo-50">
          <span className="text-6xl" aria-hidden="true">
            {item.icon || '📚'}
          </span>
        </div>
      )}
      <div className="flex flex-1 flex-col p-6">
        <div className="flex-1">
          <h2 className="text-xl font-bold text-slate-950" dir="auto">
            {item.name}
          </h2>
          {item.subtitle && (
            <p className="mt-1 text-sm font-medium text-primary-700" dir="auto">
              {item.subtitle}
            </p>
          )}
          {item.description && (
            <p className="mt-4 line-clamp-3 text-sm leading-6 text-slate-600" dir="auto">
              {item.description}
            </p>
          )}
        </div>
        <TagList tags={item.tags} className="mt-5" />
      </div>
    </article>
  );

  // When a direct cta_url is set, link straight to it with a full navigation
  // (an <a>, not a client-side <Link>) so a path-hosted app served by
  // CloudFront (e.g. /speech-therapy-test) actually loads. Otherwise route to
  // the in-catalog landing page.
  if (item.cta_url) {
    return (
      <a
        href={item.cta_url}
        data-testid={`catalog-card-${item.slug}`}
        target={item.external ? '_blank' : undefined}
        rel={item.external ? 'noopener noreferrer' : undefined}
        className={className}
      >
        {inner}
      </a>
    );
  }

  return (
    <Link to={`/${item.slug}`} data-testid={`catalog-card-${item.slug}`} className={className}>
      {inner}
    </Link>
  );
}
